import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useCurrentUserDetails } from '../../../entities/user/model';
import React, { FC, useState } from 'react';
import { AragonAddressPicker } from '../../../widgets/Payments/BatchPaymentModal.AragonAddressPicker';
import { DepositDetailsSelection } from '../Payso.DepositDetailsSelection';
import { ConfirmInWalletOverlay } from '../../../widgets/Payments/PaymentModal.ConfirmInWalletOverlay';
import {
  FailedPayment,
  SuccessConfirmation,
} from '../../../widgets/Payments/CryptoToFiatPaymentModal.SuccessConfirmation';
import { PaymentInformation } from '../../../widgets/Payments/PaymentModal.PaymentInformation';
import { WarningsSection } from '../../../widgets/Payments/PaymentModal.WarningsSection';
import { useAccount } from 'wagmi';
import { MultipleBankPaymentInformation } from '../../../widgets/Payments/CryptoToFiatPaymentModal.PaymentInformation';
import {
  CURRENCY_TYPES,
  getTokenHumanReadableNameByChain,
  NETWORK_TYPES,
  TOKEN_TYPES,
  upperCaseNetworkType,
} from '../../bill/lib';
import { ConfirmationSection } from '../../../widgets/Payments/PaymentModal.ConfirmationSection';
import { Button } from '../../../shared/ui/Button/Button';
import { createCryptoPayoutDetails } from '../PayoutDetails';
import { isDev } from '../../../shared/lib/env';
import { useBillMutations } from '../../../entities/bill/model';
import { useTransfers } from '../../transfers/useTransfers';
import { paymentProviderIdMethodsMap } from '../PaymentProviderIdTypes';
import { Modal as AModal, Flex } from 'antd';
import {
  MakeCryptoTransferButton,
  useTokenBalanceByWalletProvider,
} from '../MakeCryptoTransferButton';
import { sendTransactionLog } from '../../analytics/sendTransactionLog';
import { BankTransferPayoutDetails } from 'features/payments/PayoutDetails';
import { ExternalDepositConfirmation } from './offramp/ExternalDepositConfirmation';

const OfframpModal: FC<{
  show: boolean;
  title: string;
  payouts: BankTransferPayoutDetails[];
  onClose: () => void;
  initiatingOfframp: any;
  initiateOfframpData: any;
  initiateOfframpError: any;
  onOnrampInitiate: any;
  onExecuteOfframp: any;
  executingOfframp: any;
  executionOfframpSuccess: any;
  executionOfframpError: any;
  allowedDepositNetworks: any;
}> = ({
  show,
  title,
  payouts: payoutsParam,
  onClose,
  initiatingOfframp,
  initiateOfframpData,
  initiateOfframpError,
  onOnrampInitiate,
  onExecuteOfframp,
  executingOfframp,
  executionOfframpSuccess,
  executionOfframpError,
  allowedDepositNetworks,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const payoutsState = useState<BankTransferPayoutDetails[]>(payoutsParam);
  const payouts = payoutsState[0];
  const [depositMethod] = useState('wallet-connect');
  const [depositTokenChain, setDepositTokenChain] = useState<string>(
    payouts[0].sourceNetwork || ''
  );
  const [aragonAppAddress, setAragonAppAddress] = useState('');
  const [
    waitingForUserWalletConfirmation,
    setWaitingForUserWalletConfirmation,
  ] = useState(false);
  const { chain } = useAccount();
  const [isConfirmedByUser, setIsConfirmedByUser] = useState(false);
  const [showExternalDepositFlow, setShowExternalDepositFlow] = useState(false);
  const [isCryptoTransferError, setIsCryptoTransferError] = useState(false);

  const _getBalanceWalletProvider = () => {
    if (!depositMethod) {
      return undefined;
    }
    if (
      depositMethod === 'wallet-connect' &&
      depositTokenChain === NETWORK_TYPES.Solana
    ) {
      return 'solana-wallet-connect';
    }
    return 'wallet-connect';
  };

  const { getReadableBalanceString } = useTokenBalanceByWalletProvider(
    _getBalanceWalletProvider(),
    initiateOfframpData?.depositInfo?.token,
    initiateOfframpData?.depositInfo?.chain
  );

  const _handleDepositDetailsSelect = ({
    depositTokenChain,
  }: {
    depositTokenChain: string;
  }) => {
    setDepositTokenChain(depositTokenChain);
    onOnrampInitiate({ depositTokenChain: depositTokenChain.toLowerCase() });
  };

  const _handleConfirm = (val: boolean) => {
    setIsConfirmedByUser(val);
  };

  const _handleCryptoTransactionSuccess = ({
    // @ts-ignore
    transactionHash,
    // @ts-ignore
    safeTransactionHash,
    // @ts-ignore
    aragonProposalId,
  }) => {
    sendTransactionLog(getAccessTokenSilently, {
      title: '[Offramp][Crypto Deposit] - Success',
      transactionHash,
      safeTransactionHash,
      aragonProposalId,
    });

    onExecuteOfframp({
      transactionHash: transactionHash,
      safeTransactionHash: safeTransactionHash,
      aragonProposalId: aragonProposalId,
    });

    setWaitingForUserWalletConfirmation(false);
  };

  const _handleCryptoTransactionStart = () => {
    setWaitingForUserWalletConfirmation(true);
  };

  const _getCryptoPaymentProviderId = () => {
    if (!depositMethod) {
      return undefined;
    }

    if (depositMethod === 'aragon-app') {
      return 'aragon-app';
    }

    if (
      depositMethod === 'wallet-connect' &&
      upperCaseNetworkType(depositTokenChain) === NETWORK_TYPES.Solana
    ) {
      return 'solana-wallet-connect';
    }

    return 'wallet-connect';
  };

  const _renderMiddleStepScreen = () => {
    if (depositMethod === 'aragon-app' && !aragonAppAddress) {
      return (
        <AragonAddressPicker
          containerClassname="flex flex-col pt-1"
          // @ts-ignore
          onAddressSelect={(a) => setAragonAppAddress(a)}
          show
        />
      );
    }

    if (!depositTokenChain) {
      return (
        <DepositDetailsSelection
          // @ts-ignore
          depositNetworksOptions={allowedDepositNetworks?.map((n) => ({
            value: n,
            label: n,
          }))}
          onSelect={_handleDepositDetailsSelect}
        />
      );
    }

    if (showExternalDepositFlow) {
      return (
        <ExternalDepositConfirmation
          onClose={() => {
            setShowExternalDepositFlow(false);
          }}
          onConfirm={({ depositTransactionHash }) => {
            onExecuteOfframp({
              transactionHash: depositTransactionHash,
            });

            setShowExternalDepositFlow(false);
          }}
        />
      );
    }

    if (executingOfframp || waitingForUserWalletConfirmation) {
      return <ConfirmInWalletOverlay show />;
    }

    if (executionOfframpSuccess) {
      return <SuccessConfirmation onClose={onClose} show />;
    }

    if (
      executionOfframpError ||
      initiateOfframpError ||
      isCryptoTransferError
    ) {
      return <FailedPayment onClose={onClose} show />;
    }
  };

  const _renderTransactionDetails = () => {
    if (!depositTokenChain) {
      return null;
    }

    return (
      <>
        <MultipleBankPaymentInformation
          multipleBankDetails={payouts.map((p, index) => ({
            beneficiaryName: p.accountBeneficiary,
            accountNumber: p.accountNumber,
            bankRoutingNumber: p.accountACHRoutingNumber,
            bankSwiftCode: p.bankBIC,
            bankIban: p.accountIBAN,
          }))}
        />

        {initiateOfframpData && (
          <>
            <PaymentInformation
              title="Deposit Information"
              walletAddress={
                initiateOfframpData?.depositInfo?.depositWallet?.address
              }
              recipientLabel="Deposit Address"
              network={initiateOfframpData?.depositInfo?.chain}
              token={initiateOfframpData?.depositInfo?.token}
              amount={initiateOfframpData?.depositInfo?.amount}
              balance={getReadableBalanceString()}
            />
            <WarningsSection
              showNetworkMismatch={
                !!chain && depositTokenChain !== NETWORK_TYPES.Solana
              }
              connectedChain={chain}
              payeeToken={initiateOfframpData?.depositInfo?.token}
              payeeNetwork={initiateOfframpData?.depositInfo?.chain}
              payeeAmount={initiateOfframpData?.depositInfo?.amount}
              showHighFXRateWarning={
                payouts[0] &&
                payouts[0].accountIBAN &&
                payouts[0].currency === CURRENCY_TYPES.USD
              }
              payeeWalletAddress={
                initiateOfframpData?.depositInfo?.depositWallet?.address
              }
              paymentProps={{
                balance: getReadableBalanceString(),
              }}
            />
          </>
        )}

        <ConfirmationSection
          isConfirmed={isConfirmedByUser}
          setIsConfirmed={() => {}}
          onConfirmationChange={_handleConfirm}
        />

        <Flex vertical gap={'middle'} className="mt-5 sm:mt-6">
          {initiateOfframpData?.depositInfo && (
            <>
              <div className="text-slate-500">
                <span
                  onClick={() => {
                    setShowExternalDepositFlow(true);
                  }}
                  className={`text-indigo-400 cursor-pointer underline`}>
                  Confirm {initiateOfframpData?.depositInfo?.amount}{' '}
                  {getTokenHumanReadableNameByChain(
                    initiateOfframpData?.depositInfo?.token
                  )}{' '}
                  deposit
                </span>{' '}
                if you made it externally or continue by paying via Fractal
              </div>
            </>
          )}
          <div className="sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
            {_getCryptoPaymentProviderId() && (
              <MakeCryptoTransferButton
                cryptoPaymentProviderId={_getCryptoPaymentProviderId()}
                cryptoPaymentProviderOptions={{
                  aragonAppAddress,
                }}
                // @ts-ignore
                onTransferFail={({ error } = {}) => {
                  sendTransactionLog(getAccessTokenSilently, {
                    title: '[Offramp][Crypto Deposit] - Fail',
                    error,
                  });
                  setWaitingForUserWalletConfirmation(false);
                  setIsCryptoTransferError(true);
                }}
                disabled={!isConfirmedByUser}
                onTransferSuccess={_handleCryptoTransactionSuccess}
                onTransferStart={_handleCryptoTransactionStart}
                payouts={[
                  createCryptoPayoutDetails(
                    initiateOfframpData?.depositInfo?.amount,
                    initiateOfframpData?.depositInfo?.token,
                    initiateOfframpData?.depositInfo?.chain,
                    initiateOfframpData?.depositInfo?.depositWallet?.address
                  ),
                ]}
              />
            )}

            <Button color="gray" className="sm:order-first" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </Flex>
      </>
    );
  };

  return (
    <AModal
      title={title}
      open={show}
      footer={null}
      centered
      maskClosable={false}
      loading={initiatingOfframp}
      destroyOnClose
      onCancel={onClose}>
      <div className="pt-4" />
      {_renderMiddleStepScreen()}
      {_renderTransactionDetails()}
    </AModal>
  );
};

export const BillPaymentViaOfframpModal: FC<{
  payouts: BankTransferPayoutDetails[];
  show: boolean;
  bills: any[];
  paymentProviderConfig: any;
  onClose: () => void;
}> = ({ bills, payouts, show, onClose, paymentProviderConfig }) => {
  const { invalidate } = useBillMutations();
  const { getCurrentOrganizationId } = useCurrentUserDetails();
  const [depositTokenChain, setDepositTokenChain] = useState('');
  const initiateBillOfframpMutation = useInitiateBillOfframp(
    // @ts-ignore
    paymentProviderIdMethodsMap[paymentProviderConfig?.id],
    {
      depositTokenChain,
      billIds: bills.map((b) => b._id),
    }
  );
  const executeBillOfframpMutation = useExecuteBillOfframp();

  const onOnrampInit = ({
    depositTokenChain,
  }: {
    depositTokenChain: string;
  }) => {
    setDepositTokenChain(depositTokenChain);
  };

  const onExecuteOfframp = ({
    transactionHash,
    safeTransactionHash,
    aragonProposalId,
  }: {
    transactionHash: string;
    safeTransactionHash: string;
    aragonProposalId: string;
  }) => {
    if (!executeBillOfframpMutation.isPending) {
      executeBillOfframpMutation
        // @ts-ignore
        .mutateAsync({
          bills,
          organizationId: getCurrentOrganizationId(),
          transactionHash,
          safeTransactionHash,
          aragonProposalId,
        })
        .then(() => {
          invalidate();
        });
    }
  };

  return (
    <OfframpModal
      title={'Bill Payment'}
      onOnrampInitiate={onOnrampInit}
      onExecuteOfframp={onExecuteOfframp}
      initiateOfframpData={initiateBillOfframpMutation.data}
      show={show}
      allowedDepositNetworks={paymentProviderConfig?.getAvailableDepositNetworks()}
      onClose={onClose}
      payouts={payouts}
      initiatingOfframp={initiateBillOfframpMutation.isFetching}
      initiateOfframpError={initiateBillOfframpMutation.isError}
      executingOfframp={executeBillOfframpMutation.isPending}
      executionOfframpError={executeBillOfframpMutation.isError}
      executionOfframpSuccess={executeBillOfframpMutation.isSuccess}
    />
  );
};

export const TransferPaymentViaOfframpModal: FC<{
  payouts: BankTransferPayoutDetails[];
  show: boolean;
  transfers: any[];
  paymentProviderConfig: any;
  onClose: () => void;
}> = ({ transfers, payouts, show, onClose, paymentProviderConfig }) => {
  const { invalidate } = useTransfers();
  const { getCurrentOrganizationId } = useCurrentUserDetails();
  const [depositTokenChain, setDepositTokenChain] = useState(
    payouts[0].sourceNetwork?.toLowerCase() || ''
  );
  const initiateOfframpMutation = useInitiateTransferOfframp(
    // @ts-ignore
    paymentProviderIdMethodsMap[paymentProviderConfig?.id],
    {
      transferIds: transfers.map((tr) => tr.id),
      depositTokenChain,
    }
  );
  const executeOfframpMutation = useExecuteTransferOfframp();

  const onOnrampInit = ({
    depositTokenChain,
  }: {
    depositTokenChain: string;
  }) => {
    setDepositTokenChain(depositTokenChain);
  };

  const onExecuteOfframp = ({
    // @ts-ignore
    transactionHash,
    // @ts-ignore
    safeTransactionHash,
    // @ts-ignore
    aragonProposalId,
  }) => {
    if (!executeOfframpMutation.isPending) {
      executeOfframpMutation
        // @ts-ignore
        .mutateAsync({
          transferIds: transfers.map((tr) => tr.id),
          organizationId: getCurrentOrganizationId(),
          transactionHash,
          safeTransactionHash,
          aragonProposalId,
        })
        .then(() => {
          return invalidate();
        });
    }
  };

  return (
    <OfframpModal
      title={'Complete The Transfer'}
      onOnrampInitiate={onOnrampInit}
      allowedDepositNetworks={paymentProviderConfig.getAvailableDepositNetworks()}
      onExecuteOfframp={onExecuteOfframp}
      initiateOfframpData={initiateOfframpMutation.data}
      show={show}
      onClose={onClose}
      payouts={payouts}
      initiatingOfframp={initiateOfframpMutation.isFetching}
      initiateOfframpError={initiateOfframpMutation.isError}
      executingOfframp={executeOfframpMutation.isPending}
      executionOfframpError={executeOfframpMutation.isError}
      executionOfframpSuccess={executeOfframpMutation.isSuccess}
    />
  );
};

export const useInitiateBillOfframp = (
  providerId: string,
  {
    depositTokenChain,
    billIds,
  }: { depositTokenChain: string; billIds: string[] }
) => {
  const { getAccessTokenSilently } = useAuth0();
  const { getCurrentOrganizationId } = useCurrentUserDetails();
  return useQuery({
    queryKey: [
      'initiate-bill-offramp',
      providerId,
      depositTokenChain,
      ...billIds.map((id) => id),
    ],
    enabled:
      !!getCurrentOrganizationId() &&
      !!depositTokenChain &&
      !!billIds.length &&
      !!providerId,
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const res = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/public/offramp/bills/initiate`,
        {
          depositTokenChain,
          depositToken: 'USDC',
          billIds,
          providerId: providerId || 'bridge',
          organizationId: getCurrentOrganizationId(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (isDev() && depositTokenChain === 'polygon') {
        return {
          depositInfo: {
            amount: 0.001,
            token: TOKEN_TYPES['USDC.native_polygon'],
            chain: 'Polygon',
            depositWallet: {
              address: '0x4baB3904C07933F190Eb9d8ffd17214aD703147f',
            },
          },
        };
      }

      /** Since we hardcode deposit token for now, for our system we need explicitly say about polygon native
       * "token in our language" */
      if (
        res.data?.result &&
        depositTokenChain === 'polygon' &&
        res.data?.result?.depositInfo?.token === 'USDC'
      ) {
        res.data.result.depositInfo.token = TOKEN_TYPES['USDC.native_polygon'];
      }

      return res.data?.result;
    },
  });
};

export const useInitiateTransferOfframp = (
  providerId: string,
  {
    depositTokenChain,
    transferIds,
  }: { depositTokenChain: string; transferIds: string[] }
) => {
  const { getAccessTokenSilently } = useAuth0();
  const { getCurrentOrganizationId } = useCurrentUserDetails();

  return useQuery({
    queryKey: [
      'initiate-transfer-offramp',
      providerId,
      depositTokenChain,
      ...transferIds.map((id) => id),
    ],
    enabled:
      !!getCurrentOrganizationId() &&
      !!providerId &&
      !!depositTokenChain &&
      !!transferIds.length,
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const res = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/public/offramp/transfer/initiate`,
        {
          depositTokenChain,
          transferIds,
          providerId,
          organizationId: getCurrentOrganizationId(),
          depositToken: 'USDC',
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (isDev()) {
        if (depositTokenChain === 'polygon') {
          return {
            depositInfo: {
              amount: 0.001,
              token: TOKEN_TYPES['USDC.native_polygon'],
              chain: 'Polygon',
              depositWallet: {
                address: '0x4baB3904C07933F190Eb9d8ffd17214aD703147f',
              },
            },
          };
        }

        if (depositTokenChain === 'solana') {
          return {
            depositInfo: {
              amount: 0.001,
              token: 'USDC',
              chain: 'Solana',
              depositWallet: {
                address: '6eCGHoMYsG435eqPvPTZXCK6FFVzLNdBBYQ8rzw11hCp',
                // address: 'rk3qxNGa4smazGZaWCyTUWAsRKjdG64LNspviARnCAe', // Pavel's prod liquidation address
              },
            },
          };
        }
      }

      /** Since we hardcode deposit token for now, for our system we need explicitly say about polygon native
       * "token in our language" */
      if (
        res.data?.result &&
        depositTokenChain === 'polygon' &&
        res.data?.result?.depositInfo?.token === 'USDC'
      ) {
        res.data.result.depositInfo.token = TOKEN_TYPES['USDC.native_polygon'];
      } else if (
        res.data?.result &&
        depositTokenChain === 'arbitrum' &&
        res.data?.result?.depositInfo?.token === 'USDC'
      ) {
        res.data.result.depositInfo.token = TOKEN_TYPES['USDC.native_arbitrum'];
      }

      return res.data?.result;
    },
  });
};

export const useExecuteBillOfframp = () => {
  const { getAccessTokenSilently } = useAuth0();
  return useMutation({
    mutationKey: ['execute-bill-offramp'],

    mutationFn: async ({
      // @ts-ignore
      bills,
      // @ts-ignore
      organizationId,
      // @ts-ignore
      transactionHash,
      // @ts-ignore
      safeTransactionHash,
      // @ts-ignore
      aragonProposalId,
    }) => {
      const token = await getAccessTokenSilently();
      const res = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/public/offramp/bills/execute`,
        {
          // @ts-ignore
          billIds: bills.map((b) => b._id),
          organizationId,
          transactionHash,
          safeTransactionHash,
          aragonProposalId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return res.data?.result;
    },
  });
};

export const useExecuteTransferOfframp = () => {
  const { getAccessTokenSilently } = useAuth0();
  return useMutation({
    mutationKey: ['execute-transfer-offramp'],
    mutationFn: async ({
      // @ts-ignore
      transferIds,
      // @ts-ignore
      organizationId,
      // @ts-ignore
      transactionHash,
      // @ts-ignore
      safeTransactionHash,
      // @ts-ignore
      aragonProposalId,
    }) => {
      const token = await getAccessTokenSilently();
      const res = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/public/offramp/transfer/execute`,
        {
          transferIds,
          organizationId,
          transactionHash,
          safeTransactionHash,
          aragonProposalId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return res.data?.result;
    },
  });
};
